import {
  PUBLIC_ROOM,
  normaliseRoomCode,
  type RoomInfo,
  eventById,
  INTRO_MS,
  MARKS_LEAD,
  meetsStandard,
  pointsFor,
  sanitiseName,
  standardFor,
  type EventDef,
  type EventStartMsg,
  type LeagueRow,
  type MeetResultRow,
  type PlayerInfo,
  type Programme,
  type RecentMeet,
  type ResultRow,
  type RoomOptions,
  type ServerMessage,
  type UserStats,
  DEFAULT_ROOM_OPTIONS,
} from '@decathlon/shared';
import { inviteCode, roomLink, setRoomUrl } from './rooms.js';
import { Account } from './account.js';
import { Music, MUSIC_TRACKS, Synth, type MusicTrack } from './audio.js';
import {
  attachControls,
  bindingById,
  loadBinding,
  saveBinding,
  type ControlEvent,
  type KeyBinding,
} from './controls.js';
import { clear, h, keyLabel } from './dom.js';
import type { EventOutcome } from './events/controller.js';
import { MeetEventView } from './events/meet.js';
import { DEFAULT_KIT, isKitColour } from './game/blocky.js';
import { GameScene, VIEW_MODES } from './game/scene.js';
import { loadHistory, localStats, recordLocal } from './history.js';
import { LocalTransport, WsTransport, type Transport } from './net/client.js';
import { loadPbs, recordPb } from './pbs.js';
import { renderAbout } from './screens/about.js';
import { loadAffiliate } from './screens/affiliate.js';
import { Hud } from './screens/hud.js';
import { renderLobby } from './screens/lobby.js';
import { renderMenu } from './screens/menu.js';
import { renderMeetIntro } from './screens/result.js';
import { renderEventResults, renderLeague, renderPodium } from './screens/standings.js';
import { renderStats } from './screens/stats.js';

type Screen =
  'menu' | 'about' | 'stats' | 'league' | 'lobby' | 'intro' | 'run' | 'results' | 'podium';

const PREF = 'decathlon.prefs';
interface Prefs {
  name: string;
  kit: string;
  practiceBots: boolean;
}
function loadPrefs(): Prefs {
  try {
    const p = JSON.parse(localStorage.getItem(PREF) ?? '{}') as Partial<Prefs>;
    return {
      name: p.name ?? '',
      kit: p.kit && isKitColour(p.kit) ? p.kit : DEFAULT_KIT,
      practiceBots: p.practiceBots ?? true,
    };
  } catch {
    return { name: '', kit: DEFAULT_KIT, practiceBots: true };
  }
}

const ME_PREVIEW = 'preview';

export class App {
  ui = document.getElementById('ui')!;
  scene = new GameScene(document.getElementById('game')!);
  synth = new Synth();
  music = new Music();
  account = new Account();
  authMode: 'none' | 'login' | 'register' = 'none';
  authBusy = false;
  authPassword = '';
  authMessage = '';
  authError = false;
  prefs = loadPrefs();
  binding: KeyBinding = loadBinding();
  screen: Screen = 'menu';
  prevScreen: Screen = 'menu';
  detach: (() => void) | null = null;
  hud: Hud | null = null;
  stats: UserStats | null = null;

  roomInfo: RoomInfo | null = null;
  roomCode = inviteCode();
  roomError = '';

  // Meet
  transport: Transport | null = null;
  myId: string | null = null;
  joined = false;
  status = 'Not connected';
  players: PlayerInfo[] = [];
  options: RoomOptions = { ...DEFAULT_ROOM_OPTIONS };
  phase = 'lobby';
  serverOffset = 0;
  pingTimer: ReturnType<typeof setInterval> | null = null;
  current: EventStartMsg | null = null;
  view: MeetEventView | null = null;
  introTimer: ReturnType<typeof setTimeout> | null = null;
  eventResults: Extract<ServerMessage, { type: 'eventResults' }> | null = null;
  outcome: EventOutcome | null = null;
  lastPb = false;
  podium: { results: MeetResultRow[]; programme: Programme } | null = null;
  league: { rows: LeagueRow[]; recent: RecentMeet[] } = { rows: [], recent: [] };
  /** Practice: programme to start as soon as the local room says hello. */
  pendingPractice: Programme | null = null;

  constructor() {
    this.scene.reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scene.onFrame = (dt) => this.frame(dt);
    this.showPreview();
    const kick = () => {
      this.music.play();
      removeEventListener('pointerdown', kick);
      removeEventListener('keydown', kick);
    };
    addEventListener('pointerdown', kick);
    addEventListener('keydown', kick);
    this.detach = attachControls(
      this.binding,
      (e) => this.onControl(e),
      () => this.screen === 'run',
    );
    void this.account.verify().then(() => this.render());
    void loadAffiliate().then((ads) => this.scene.setAds(ads));
    this.scene.onBoardClick = (board) => {
      if (board.url) window.open(board.url, '_blank', 'noopener');
      else
        this.toast(
          board.kind === 'advertise'
            ? 'Want your brand on these hoardings? Contact the site owner.'
            : 'Link unavailable',
          false,
        );
    };
    this.render();
  }

  /** The menu athlete standing at the blocks. */
  private showPreview(): void {
    this.scene.clearAthletes();
    const a = this.scene.addAthlete(ME_PREVIEW, this.prefs.kit, 3);
    a.setPose('idle');
    a.setHeight(0);
    this.scene.setFocus(ME_PREVIEW);
    this.scene.mode = 'menu';
  }

  // ----------------------------------------------------------- settings
  savePrefs(): void {
    try {
      localStorage.setItem(PREF, JSON.stringify(this.prefs));
    } catch {
      /* ignore */
    }
  }
  viewName(): string {
    return VIEW_MODES.find((v) => v.id === this.scene.view)?.name ?? 'Side';
  }
  musicName(): string {
    return this.music.enabled
      ? (MUSIC_TRACKS.find((t) => t.id === this.music.track)?.name ?? 'On')
      : 'Off';
  }
  cycleView(): void {
    this.scene.cycleView();
    this.hud?.setViewName(this.viewName());
    if (this.screen === 'menu') this.render();
  }
  setMusicOn(on: boolean): void {
    this.music.setEnabled(on);
    this.music.play();
    this.hud?.setMusicName(this.musicName());
    if (this.screen === 'menu') this.render();
  }
  setMusic(t: MusicTrack): void {
    this.music.setTrack(t);
    this.music.play();
    this.hud?.setMusicName(this.musicName());
  }
  setSfx(on: boolean): void {
    this.synth.setMuted(!on);
    if (on && this.screen === 'run') this.synth.crowdStart();
    this.hud?.setSfx(on);
    if (this.screen === 'menu') this.render();
  }
  setBinding(id: string): void {
    this.binding = bindingById(id);
    saveBinding(id);
    this.detach?.();
    this.detach = attachControls(
      this.binding,
      (e) => this.onControl(e),
      () => this.screen === 'run',
    );
    this.render();
  }
  playerName(): string | null {
    return this.account.name ?? sanitiseName(this.prefs.name);
  }
  serverNow(): number {
    return Date.now() + this.serverOffset;
  }

  // ----------------------------------------------------------- account
  async submitAuth(name: string, password: string): Promise<void> {
    if (this.authBusy) return;
    const mode = this.authMode;
    this.authPassword = password;
    this.authMessage = '';
    this.authBusy = true;
    this.render();
    try {
      if (mode === 'register') await this.account.register(name, password);
      else await this.account.login(name, password);
      this.prefs.name = this.account.name ?? this.prefs.name;
      this.savePrefs();
      this.authMode = 'none';
      this.authPassword = '';
      this.authMessage = `Signed in as ${this.account.name}`;
      this.authError = false;
    } catch (e) {
      this.authMessage = e instanceof Error ? e.message : 'Sign in failed';
      this.authError = true;
    } finally {
      this.authBusy = false;
      this.render();
    }
  }

  async showStats(): Promise<void> {
    const name = this.playerName() ?? 'Guest';
    if (this.account.token) {
      try {
        this.stats = await this.account.stats();
      } catch (e) {
        this.toast(e instanceof Error ? e.message : 'Could not load stats');
        this.stats = localStats(name);
      }
    } else this.stats = localStats(name);
    if (this.screen !== 'stats') this.prevScreen = this.screen;
    this.screen = 'stats';
    this.render();
  }

  showLeague(): void {
    if (this.screen !== 'league') this.prevScreen = this.screen;
    this.screen = 'league';
    if (this.transport) this.transport.send({ type: 'requestLeague' });
    else {
      void fetch('/api/league')
        .then((r) => r.json())
        .then((l: { rows: LeagueRow[]; recent: RecentMeet[] }) => {
          this.league = l;
          if (this.screen === 'league') this.render();
        })
        .catch(() => undefined);
    }
    this.render();
  }

  // ----------------------------------------------------------- transport
  private connect(t: Transport, entry: { room?: string; createRoom?: boolean } = {}): void {
    const previous = this.transport;
    this.transport = t;
    previous?.close();
    this.roomInfo = null;
    this.joined = false;
    this.myId = null;
    this.players = [];
    this.serverOffset = 0;
    t.onStatus((s) => {
      if (this.transport !== t) return;
      this.status =
        t.kind === 'local'
          ? 'Practice'
          : s === 'open'
            ? 'Connected'
            : s === 'connecting'
              ? 'Connecting…'
              : 'Disconnected, retrying…';
      if (s === 'closed' && t.kind === 'online') {
        this.joined = false;
        this.myId = null;
        this.players = [];
        if (this.screen !== 'menu') this.toLobbyScreen();
      }
      if (this.screen === 'lobby') this.render();
    });
    t.onMessage((m) => {
      if (this.transport === t) this.onMessage(m);
    });
    t.connect();
    t.send({
      type: 'join',
      ...entry,
      name: this.playerName() ?? '',
      kit: this.prefs.kit,
      ...(this.account.token ? { token: this.account.token } : {}),
    });
    if (this.pingTimer) clearInterval(this.pingTimer);
    if (t.kind === 'online')
      this.pingTimer = setInterval(() => t.send({ type: 'ping', t: Date.now() }), 5000);
  }

  private onMessage(m: ServerMessage): void {
    switch (m.type) {
      case 'welcome':
        this.myId = m.id;
        this.joined = true;
        if (this.transport?.kind === 'online' && m.room) {
          this.roomInfo = m.room;
          this.roomCode = m.room.code;
          setRoomUrl(m.room.code);
        }
        if (this.transport?.kind === 'online') this.serverOffset = m.serverTime - Date.now();
        break;
      case 'pong': {
        const rtt = Date.now() - m.t;
        this.serverOffset = m.serverTime + rtt / 2 - Date.now();
        break;
      }
      case 'lobby':
        this.players = m.players;
        this.options = m.options;
        this.phase = m.phase;
        if (m.phase === 'lobby') {
          if (this.pendingPractice && this.joined) {
            const programme = this.pendingPractice;
            this.pendingPractice = null;
            this.transport?.send({
              type: 'setOptions',
              options: { programme, fillWithBots: this.prefs.practiceBots, botLevel: 'county' },
            });
            this.transport?.send({ type: 'start' });
          } else if (this.screen === 'league') this.prevScreen = 'lobby';
          else this.toLobbyScreen();
        } else if (m.phase === 'event' && this.current && this.screen === 'intro')
          this.beginEvent();
        if (this.screen === 'lobby') this.render();
        break;
      case 'eventStart':
        this.onEventStart(m);
        break;
      case 'turn':
        this.view?.onTurn(m);
        break;
      case 'correction':
        if (m.index === this.current?.index) this.view?.correct(m.sim, m.ack);
        break;
      case 'snapshot':
        this.view?.onSnapshot(m.athletes, m.activeId, m.serverTime);
        break;
      case 'eventResults':
        this.onEventResults(m);
        break;
      case 'podium':
        this.endEventView();
        this.podium = { results: m.results, programme: m.programme };
        this.screen = 'podium';
        this.scene.mode = 'result';
        this.synth.fanfare();
        this.synth.announce('The meet is over. Congratulations to our medallists.');
        this.render();
        break;
      case 'league':
        this.league = { rows: m.rows, recent: m.recent };
        if (this.screen === 'league') this.render();
        break;
      case 'error':
        if (!this.joined && this.transport) {
          this.leave(true);
          this.roomError = m.message;
          this.render();
        } else this.toast(m.message);
        break;
    }
  }

  // ----------------------------------------------------------- flow
  practice(what: EventDef | Programme): void {
    if (!this.requireName()) return;
    this.pendingPractice = typeof what === 'string' ? what : what.id;
    this.connect(new LocalTransport());
    this.screen = 'lobby';
    this.render();
  }

  playOnline(code = PUBLIC_ROOM, create = false): void {
    if (!this.requireName()) return;
    const room = code.trim().toLowerCase() === PUBLIC_ROOM ? PUBLIC_ROOM : normaliseRoomCode(code);
    if (!create && !room) {
      this.roomError = 'Enter the four-character room code.';
      this.render();
      return;
    }
    this.roomError = '';
    this.connect(
      new WsTransport(create ? 'new' : room!),
      create ? { createRoom: true } : { room: room! },
    );
    this.screen = 'lobby';
    this.render();
  }

  private requireName(): boolean {
    if (!this.playerName()) {
      this.toast('Enter a name of 2 to 16 letters or numbers first.');
      return false;
    }
    if (!this.account.name) {
      this.prefs.name = this.playerName()!;
      this.savePrefs();
    }
    return true;
  }

  private toLobbyScreen(): void {
    this.endEventView();
    this.screen = 'lobby';
    this.showPreview();
    this.scene.stadium.setBoard(['DECATHLON', '', 'LOBBY']);
    this.render();
  }

  private onEventStart(m: EventStartMsg): void {
    this.endEventView();
    this.current = m;
    this.eventResults = null;
    this.outcome = null;
    this.screen = 'intro';
    this.scene.mode = 'menu';
    this.showPreview();
    this.scene.stadium.setBoard([
      eventById(m.event).name.toUpperCase(),
      `EVENT ${m.index + 1} OF ${m.total}`,
      'GET READY',
    ]);
    this.synth.announce(`Event ${m.index + 1}: ${eventById(m.event).name}.`);
    this.render();
    // Fallback if the lobby phase message is late: the event begins when the intro ends.
    const eventAt = m.refAt - (m.turnBased ? 0 : MARKS_LEAD * 1000);
    if (this.introTimer) clearTimeout(this.introTimer);
    this.introTimer = setTimeout(
      () => this.screen === 'intro' && this.beginEvent(),
      Math.max(0, eventAt - this.serverNow()) + 50,
    );
  }

  private beginEvent(): void {
    const start = this.current;
    if (!start || this.view) return;
    const ev = eventById(start.event);
    this.screen = 'run';
    clear(this.ui);
    this.scene.clearAthletes();
    this.hud = new Hud(
      this.ui,
      {
        title: ev.name,
        subtitle: `Event ${start.index + 1} of ${start.total}`,
        binding: this.binding,
        viewName: this.viewName(),
        musicName: this.musicName(),
        sfxOn: !this.synth.muted,
        onView: () => this.cycleView(),
        onMusic: () => this.setMusicOn(!this.music.enabled),
        onSfx: () => this.setSfx(this.synth.muted),
        onQuit: () => this.leave(),
        onRun: (key) => this.onControl({ type: 'run', key }),
        onAction: (down) => this.onControl({ type: 'action', down }),
        onHeight: (height) => this.transport?.send({ type: 'setHeight', height }),
        onSkip: () => this.skipBot(),
      },
      start.turnBased || start.event === '110h',
    );
    this.view = new MeetEventView({
      scene: this.scene,
      synth: this.synth,
      hud: this.hud,
      meId: this.myId,
      players: this.players,
      start,
      serverNow: () => this.serverNow(),
      send: (batch) => this.transport?.send({ type: 'input', batch }),
    });
    this.view.begin();
  }

  private onEventResults(m: Extract<ServerMessage, { type: 'eventResults' }>): void {
    const ev = eventById(m.event);
    this.outcome = this.view?.finishOutcome() ?? null;
    this.endEventView();
    this.eventResults = m;
    const me = m.results.find((r) => r.id === this.myId);
    this.lastPb = false;
    if (me && me.participated !== false) {
      this.lastPb = me.mark !== null && recordPb(ev, me.mark);
      const row: ResultRow = {
        resultKey: `${m.meetId}:${m.index}:${me.id}`,
        playedAt: new Date().toISOString(),
        event: ev.id,
        mark: me.mark,
        points: pointsFor(ev, me.mark),
        mode: this.transport?.kind === 'online' ? 'online' : 'solo',
        ...(this.outcome ? { cadence: this.outcome.stats } : {}),
      };
      const recorded = recordLocal(row);
      // Online meets are recorded by the server for accounts; practice results we post ourselves.
      if (recorded && this.transport?.kind === 'local') void this.account.postResult(row);
      if (this.lastPb) this.synth.fanfare();
      else if (!meetsStandard(ev, me.mark, this.current?.season ?? 1)) this.synth.fail();
    }
    this.screen = 'results';
    this.scene.mode = 'result';
    this.render();
  }

  private endEventView(): void {
    this.view?.dispose();
    this.view = null;
    this.hud?.destroy();
    this.hud = null;
    this.synth.crowdStop();
    if (this.introTimer) clearTimeout(this.introTimer);
    this.introTimer = null;
  }

  leave(keepInvite = false): void {
    this.endEventView();
    const previous = this.transport;
    this.transport = null;
    previous?.close();
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
    this.joined = false;
    this.myId = null;
    this.players = [];
    this.current = null;
    this.pendingPractice = null;
    this.status = 'Not connected';
    this.roomInfo = null;
    if (!keepInvite) {
      this.roomCode = '';
      this.roomError = '';
      setRoomUrl(null);
    }
    this.toMenu();
  }

  toMenu(): void {
    this.endEventView();
    this.screen = 'menu';
    this.showPreview();
    this.scene.stadium.setBoard(['DECATHLON', '', 'WELCOME']);
    this.scene.stadium.excitement = 0;
    this.render();
  }

  onControl(e: ControlEvent): void {
    if (e.type === 'camera') return this.cycleView();
    if (e.type === 'quit') {
      if (this.screen === 'stats' || this.screen === 'league' || this.screen === 'about') {
        this.screen =
          this.prevScreen === this.screen || this.prevScreen === 'run'
            ? this.transport
              ? 'lobby'
              : 'menu'
            : this.prevScreen;
        if (this.screen === 'menu') this.toMenu();
        else this.render();
        return;
      }
      if (this.screen !== 'menu') this.leave();
      return;
    }
    if (e.type === 'confirm') {
      if (this.screen === 'run') this.skipBot();
      return;
    }
    if (this.screen === 'run') this.view?.control(e);
  }

  /** Field events: skip the bot whose turn it is (the room fast-forwards its attempt). */
  skipBot(): void {
    if (this.view?.canSkip()) this.transport?.send({ type: 'skipBot' });
  }

  frame(dt: number): void {
    if (this.screen === 'run' && this.view) {
      this.view.frame(dt);
      return;
    }
    const p = this.scene.getAthlete(ME_PREVIEW);
    p?.update(dt, 0, 10);
  }

  toast(msg: string, error = true): void {
    const t = h('div', { class: `toast${error ? '' : ' ok'}`, role: 'alert' }, msg);
    this.ui.append(t);
    setTimeout(() => t.remove(), 3500);
  }

  // ----------------------------------------------------------- render
  render(): void {
    if (this.screen === 'run') return;
    clear(this.ui);
    const online = this.transport?.kind === 'online';
    if (this.screen === 'about')
      return renderAbout(this.ui, () => this.onControl({ type: 'quit' }));
    if (this.screen === 'stats' && this.stats)
      return renderStats(this.ui, this.stats, !this.account.token, () =>
        this.onControl({ type: 'quit' }),
      );
    if (this.screen === 'league')
      return renderLeague(
        this.ui,
        this.league.rows,
        this.league.recent,
        this.playerName() ?? '',
        () => this.onControl({ type: 'quit' }),
      );
    if (this.screen === 'intro' && this.current) {
      const ev = eventById(this.current.event);
      return renderMeetIntro(this.ui, {
        ev,
        index: this.current.index,
        total: this.current.total,
        standard: standardFor(ev, this.current.season),
        pb: loadPbs()[ev.id],
        leftKey: keyLabel(this.binding.left),
        rightKey: keyLabel(this.binding.right),
        eventAt: this.current.refAt - (this.current.turnBased ? 0 : MARKS_LEAD * 1000),
        serverNow: () => this.serverNow(),
        turnBased: this.current.turnBased,
        introMs: INTRO_MS,
      });
    }
    if (this.screen === 'results' && this.eventResults) {
      const m = this.eventResults;
      return renderEventResults(
        this.ui,
        {
          ...m,
          myId: this.myId,
          serverNow: () => this.serverNow(),
          outcome: this.outcome,
          isPb: this.lastPb,
        },
        { leave: () => this.leave() },
      );
    }
    if (this.screen === 'podium' && this.podium) {
      return renderPodium(
        this.ui,
        {
          ...this.podium,
          myId: this.myId,
          online,
          isHost: this.players.some((p) => p.id === this.myId && p.isHost),
        },
        {
          again: () => {
            if (online) {
              this.transport?.send({ type: 'backToLobby' });
              this.toLobbyScreen();
            } else {
              const programme = this.podium!.programme;
              this.leave();
              this.practice(programme);
            }
          },
          nextSeason:
            this.podium.programme === 'full' &&
            this.players.find((p) => p.id === this.myId)?.isHost &&
            this.current?.index === 9
              ? () => this.transport?.send({ type: 'nextSeason' })
              : undefined,
          league: () => this.showLeague(),
          menu: () => this.leave(),
        },
      );
    }
    if (this.screen === 'lobby') {
      return renderLobby(
        this.ui,
        {
          status: this.status,
          myId: this.myId,
          players: this.players,
          options: this.options,
          online,
          room: this.roomInfo,
          inviteLink: this.roomInfo ? roomLink(this.roomInfo.code) : undefined,
        },
        {
          ready: (r) => this.transport?.send({ type: 'ready', ready: r }),
          start: () => this.transport?.send({ type: 'start' }),
          setOptions: (o) => this.transport?.send({ type: 'setOptions', options: o }),
          leave: () => this.leave(),
          showLeague: () => this.showLeague(),
        },
      );
    }
    renderMenu(
      this.ui,
      {
        roomCode: this.roomCode,
        roomError: this.roomError,
        name: this.prefs.name,
        account: this.account.name,
        authMode: this.authMode,
        authBusy: this.authBusy,
        authPassword: this.authPassword,
        authMessage: this.authMessage,
        authError: this.authError,
        kit: this.prefs.kit,
        binding: this.binding,
        view: this.scene.view,
        musicOn: this.music.enabled,
        music: this.music.track,
        sfxOn: !this.synth.muted,
        paOn: this.synth.pa,
        pbs: loadPbs(),
        resultCount: loadHistory().length,
        practiceBots: this.prefs.practiceBots,
      },
      {
        practice: (what) => this.practice(what),
        playOnline: () => this.playOnline(),
        createRoom: () => this.playOnline(PUBLIC_ROOM, true),
        joinRoom: () => this.playOnline(this.roomCode),
        setRoomCode: (code) => {
          this.roomCode = code;
        },
        setPracticeBots: (on) => {
          this.prefs.practiceBots = on;
          this.savePrefs();
          this.render();
        },
        setName: (n) => {
          this.prefs.name = n;
          this.savePrefs();
        },
        setAuthMode: (mode) => {
          if (this.authBusy) return;
          this.authMode = mode;
          this.authMessage = '';
          this.authPassword = '';
          this.render();
        },
        setAuthPassword: (password) => {
          this.authPassword = password;
        },
        submitAuth: (n, p) => void this.submitAuth(n, p),
        signOut: () => {
          this.authMessage = '';
          this.authPassword = '';
          void this.account.logout().then(() => this.render());
          this.render();
        },
        showStats: () => void this.showStats(),
        showLeague: () => this.showLeague(),
        setKit: (hex) => {
          this.prefs.kit = hex;
          this.savePrefs();
          this.scene.getAthlete(ME_PREVIEW)?.setKit(hex);
          this.transport?.send({ type: 'setKit', kit: hex });
          this.render();
        },
        setBinding: (id) => this.setBinding(id),
        setView: (v) => {
          this.scene.setView(v);
          this.render();
        },
        setMusicOn: (on) => this.setMusicOn(on),
        setMusic: (t) => this.setMusic(t),
        setSfx: (on) => this.setSfx(on),
        setPa: (on) => {
          this.synth.setPa(on);
          this.render();
        },
        about: () => {
          this.prevScreen = this.screen;
          this.screen = 'about';
          this.render();
        },
      },
    );
    this.ui.append(h('div', { class: 'status' }, `v${__APP_VERSION__}`));
  }
}

if (import.meta.env.MODE !== 'test')
  try {
    const app = new App();
    (window as unknown as { app: App }).app = app;
  } catch (e) {
    console.error(e);
    const ui = document.getElementById('ui')!;
    clear(ui);
    ui.append(
      h(
        'div',
        { class: 'center' },
        h(
          'div',
          { class: 'panel stack', role: 'alert' },
          h('h1', {}, 'The stadium could not start'),
          h(
            'p',
            {},
            'This game needs WebGL. Enable graphics acceleration in your browser or try another browser.',
          ),
          h('button', { class: 'btn primary', onclick: () => location.reload() }, 'Try again'),
        ),
      ),
    );
  }
